// The membership page: what a subscription is, the two plans, and — for a
// reader who already holds one — what they hold and how to end it.
//
// One page rather than a picker plus a separate account screen. A reader
// arrives here from a spent allowance or from the paywall, and the question is
// always the same one ("what do I have, what could I have"); splitting that
// across two routes would mean the answer is never on screen at once.
//
// The design continues the library's printed idiom rather than importing a
// SaaS pricing table: these are BORROWER'S CARDS — ruled stock, a letterpress
// eyebrow, the price struck in Rockwell numerals, and a rotated rubber stamp on
// the card the reader already holds. The card that lifts the paywall sits
// slightly proud of the other one, which is the only hierarchy the page needs;
// there is no "MOST POPULAR" ribbon, because two plans do not need a sales
// funnel, they need to be legible.

import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useAction, useQuery } from "convex/react";
import {
  ArrowLeft,
  Check,
  Loader2,
  PauseCircle,
  PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@bindery/convex/_generated/api";
import { AppNav } from "@/components/app-nav";
import { Button } from "@bindery/ui/components/ui/button";
import { signIn, useIsAuthenticated } from "@/lib/auth";
import { errorMessage } from "@/lib/convex-error";
import { useLogger, useLoggerContext } from "@/logging";
import { checkoutAttribution } from "@/lib/attribution";
import { formatPrice } from "@/lib/price";
import { useCoarseNow } from "@/lib/use-coarse-now";
import { cn } from "@/lib/utils";

/**
 * Ruled-card stock: the faint horizontal lines of a library borrower's card,
 * drawn in the border ink so it reads in both themes.
 *
 * Held at roughly half the border's strength deliberately. At full strength
 * the rules cross the display face and the price numerals hard enough to read
 * as a MISPRINT rather than as stock — the eye tries to align the type to the
 * lines and finds it doesn't. Faded, they sit behind the text as texture,
 * which is what card stock actually does.
 *
 * Written as ONE literal string, never assembled from parts: Tailwind's
 * scanner reads source text, so an interpolated class name is a class name it
 * never generates.
 */
const RULED =
  "bg-[repeating-linear-gradient(to_bottom,transparent_0px,transparent_31px,color-mix(in_oklab,var(--border)_55%,transparent)_31px,color-mix(in_oklab,var(--border)_55%,transparent)_32px)]";

function safeNextPath(raw: string | null): string {
  if (raw === null) return "/";
  return raw.startsWith("/") && !raw.startsWith("//") ? raw : "/";
}

/**
 * The word on the stamp. Every status gets one: reading "Lapsed" over a paused
 * subscription (which exists, is resumable, and IS on file) was the single most
 * misleading thing on this page.
 */
function stampLabel(row: { status: string; live: boolean }): string {
  if (row.status === "paused") return "Paused";
  if (row.status === "canceled") return row.live ? "Ending" : "Ended";
  if (row.status === "past_due") return row.live ? "Payment due" : "Lapsed";
  return row.live ? "Current" : "Lapsed";
}

/** What happens next, in the price line. A `past_due` subscription inside its
 *  window must not say "renews" directly above "the payment didn't go
 *  through" — the two sentences contradicted each other. */
function periodClause(
  row: {
    status: string;
    live: boolean;
    cancelAtPeriodEnd: boolean;
    pauseAtPeriodEnd: boolean;
  },
  day: string,
): string {
  if (!row.live) return `ended ${day}`;
  if (row.status === "past_due") return `paid through ${day}`;
  if (row.cancelAtPeriodEnd || row.pauseAtPeriodEnd) {
    return `access through ${day}`;
  }
  return `renews ${day}`;
}

function formatDay(ms: number): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(
    new Date(ms),
  );
}

export function SubscribePage() {
  const [params] = useSearchParams();
  const next = safeNextPath(params.get("next"));
  const isAuthenticated = useIsAuthenticated();
  const now = useCoarseNow();
  const plans = useQuery(api.subscriptions.availablePlans, {});
  const mine = useQuery(
    api.subscriptions.mySubscriptions,
    isAuthenticated ? { now } : "skip",
  );
  const subscribe = useAction(api.subscriptions.createSubscriptionSession);
  const refreshMine = useAction(api.subscriptions.refreshMine);
  const log = useLogger();
  // See `premium-gate`: the lead id this session is logging under, not a
  // re-read of storage that a restricted webview would answer with nothing.
  const { leadId } = useLoggerContext();
  const logRef = useRef(log);
  useEffect(() => {
    logRef.current = log;
  });

  const [busyPlan, setBusyPlan] = useState<string | null>(null);

  // Catch a renewal (or a lapse) we have not polled yet. A genuine external
  // side-effect, and throttled server-side by `shouldRefresh`, so landing here
  // repeatedly costs nothing — react-useeffect-discipline's sanctioned case.
  useEffect(() => {
    if (!isAuthenticated) return;
    let active = true;
    void (async () => {
      try {
        await refreshMine({});
      } catch {
        // A refresh failure is invisible by design: the stored window still
        // governs access, and the nightly sweep will try again.
        if (!active) return;
      }
    })();
    return () => {
      active = false;
    };
  }, [isAuthenticated, refreshMine]);

  useEffect(() => {
    logRef.current.event("subscribe.view", { authed: isAuthenticated });
  }, [isAuthenticated]);

  const held = mine ?? [];
  // Hide any plan that would not actually improve on what the reader already
  // holds — not just the identical one. An all-access subscriber shown the $4
  // card is being offered a strictly worse plan for another $4 a month, with
  // no proration and nothing anywhere saying it replaces anything.
  const holdsAllAccess = held.some((row) => row.live && row.allAccess);
  const livePlanIds = new Set(
    held.filter((row) => row.live).map((row) => row.planId),
  );
  const offerable = (plan: { planId: string; allAccess: boolean }): boolean =>
    !livePlanIds.has(plan.planId) && (plan.allAccess || !holdsAllAccess);

  const onSubscribe = async (planId: string): Promise<void> => {
    if (!isAuthenticated) {
      logRef.current.event("subscribe.signin", { planId });
      signIn();
      return;
    }
    setBusyPlan(planId);
    try {
      // See `premium-gate`: our own origin so checkout returns the reader to
      // the mirror holding their session. Server-side allow-listed.
      const result = await subscribe({
        planId,
        next,
        origin: window.location.origin,
        // See `premium-gate`: the stored ad click, re-validated server-side.
        // It matters more on this path — it is what every future renewal of
        // this subscription will be credited to.
        attribution: checkoutAttribution(leadId),
      });
      switch (result.kind) {
        case "redirect":
          window.location.assign(result.checkoutUrl);
          return; // navigating away — hold the busy state
        case "already_subscribed":
          toast.success("You're already subscribed to this plan.");
          break;
        case "retryable_error":
          toast.error("Checkout is busy", {
            description: "Please try again in a moment.",
          });
          break;
        case "failed":
          toast.error("Couldn't start checkout", {
            description: "Something went wrong on our end.",
          });
          break;
      }
    } catch (error) {
      toast.error("Couldn't start checkout", {
        description: errorMessage(error, "Please try again."),
      });
    } finally {
      setBusyPlan(null);
    }
  };

  return (
    <div className="bg-background min-h-svh">
      <header className="border-border flex items-center gap-2 border-b px-3 py-2">
        <Button asChild variant="ghost" size="icon-sm" aria-label="Back">
          <Link to={next}>
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <p className="font-display text-foreground min-w-0 flex-1 truncate text-sm font-bold">
          Membership
        </p>
        {/* This page owns its header, so it owns the site controls too — it
            sits outside `ChromeLayout` for exactly that reason (App.tsx). */}
        <AppNav variant="inline" />
      </header>

      <main className="mx-auto max-w-3xl px-6 pt-14 pb-24">
        <p className="text-muted-foreground font-serif text-xs tracking-[0.35em] uppercase">
          Retells · Reading room
        </p>
        <h1 className="font-display text-foreground mt-6 max-w-2xl text-4xl font-black tracking-tight text-balance sm:text-5xl">
          Read closely, look up freely
        </h1>
        <p className="text-muted-foreground mt-5 max-w-xl font-serif text-lg leading-relaxed text-pretty">
          Every signed-in reader gets five translations an hour, and every
          passage somebody has already looked up stays free for everyone, signed
          in or not, forever. A membership is for the hours when five isn’t
          enough.
        </p>

        {held.length > 0 && (
          <section className="mt-12">
            <h2 className="text-muted-foreground font-serif text-xs tracking-[0.3em] uppercase">
              Your card
            </h2>
            <div className="mt-4 flex flex-col gap-4">
              {held.map((row) => (
                <HeldCard key={row.orderId} row={row} />
              ))}
            </div>
          </section>
        )}

        <section className="mt-12">
          {held.length > 0 && (
            <h2 className="text-muted-foreground font-serif text-xs tracking-[0.3em] uppercase">
              Other plans
            </h2>
          )}
          <div className="mt-4 grid gap-5 sm:grid-cols-2 sm:items-end">
            {plans === undefined
              ? [0, 1].map((i) => (
                  <div
                    key={i}
                    className="border-border bg-card/40 h-72 animate-pulse rounded-lg border"
                  />
                ))
              : plans
                  .filter(offerable)
                  .map((plan) => (
                    <PlanCard
                      key={plan.planId}
                      plan={plan}
                      busy={busyPlan === plan.planId}
                      disabled={busyPlan !== null}
                      authed={isAuthenticated}
                      onSubscribe={() => void onSubscribe(plan.planId)}
                    />
                  ))}
          </div>
        </section>

        {/* Full-strength muted, not /80: this is the only place the
            cancellation terms are stated, and at 12px the faded token
            composites to roughly 3.4:1 — under the 4.5:1 floor. */}
        <p className="text-muted-foreground mt-10 font-serif text-xs tracking-wide">
          Monthly · cancel any time from this page, and keep the month you paid
          for · secure checkout at checkout.sara.ai
        </p>
      </main>
    </div>
  );
}

/** One plan, as a borrower's card. The all-access card sits proud of the
 *  other — the only hierarchy two plans need. */
function PlanCard({
  plan,
  busy,
  disabled,
  authed,
  onSubscribe,
}: {
  plan: {
    planId: string;
    displayName: string;
    tagline: string;
    amountCents: number;
    currency: string;
    allAccess: boolean;
  };
  busy: boolean;
  disabled: boolean;
  authed: boolean;
  onSubscribe: () => void;
}) {
  return (
    <article
      className={cn(
        "relative flex flex-col overflow-hidden rounded-lg border bg-card p-6",
        RULED,
        plan.allAccess
          ? "border-foreground/25 shadow-lg sm:pb-8"
          : "border-border shadow-sm",
      )}
    >
      <p className="text-muted-foreground font-serif text-xs tracking-[0.3em] uppercase">
        {plan.allAccess ? "Full membership" : "Lookups"}
      </p>
      <h3 className="font-display text-foreground mt-3 text-2xl font-black tracking-tight">
        {plan.displayName}
      </h3>
      <p className="text-muted-foreground mt-2 font-serif text-sm leading-relaxed text-pretty">
        {plan.tagline}
      </p>

      <p className="mt-6 flex items-baseline gap-1.5">
        <span className="font-rockwell text-foreground text-4xl tabular-nums">
          {formatPrice(plan.amountCents, plan.currency)}
        </span>
        <span className="text-muted-foreground font-serif text-sm">
          / month
        </span>
      </p>

      <ul className="mt-6 flex-1 space-y-2">
        {(plan.allAccess
          ? [
              "Every retelling, every chapter",
              "Unlimited translations",
              "Books you already bought stay yours",
            ]
          : [
              "50 translations an hour, 300 a day",
              "Every book you can already read",
              "Paid chapters still sold one book at a time",
            ]
        ).map((line) => (
          <li key={line} className="flex items-start gap-2">
            <Check
              aria-hidden="true"
              className="text-muted-foreground mt-1 size-3.5 shrink-0"
            />
            <span className="text-foreground font-serif text-sm leading-relaxed">
              {line}
            </span>
          </li>
        ))}
      </ul>

      <Button
        className="mt-7 w-full"
        size="lg"
        variant={plan.allAccess ? "default" : "outline"}
        disabled={disabled}
        onClick={onSubscribe}
      >
        {busy && <Loader2 className="size-4 animate-spin" />}
        {authed ? "Subscribe" : "Sign in to subscribe"}
      </Button>
    </article>
  );
}

/** A subscription the reader holds — stamped, with the one control that
 *  matters and an honest sentence about what happens next. */
function HeldCard({
  row,
}: {
  row: {
    orderId: string;
    planId: string;
    displayName: string;
    status: string;
    live: boolean;
    entitledThrough: number | null;
    cancelAtPeriodEnd: boolean;
    pauseAtPeriodEnd: boolean;
    amountCents: number;
    currency: string;
    checkoutUrl: string | null;
  };
}) {
  const manage = useAction(api.subscriptions.manageSubscription);
  const [busy, setBusy] = useState(false);
  const ending = row.cancelAtPeriodEnd || row.pauseAtPeriodEnd;
  const resumable = row.pauseAtPeriodEnd || row.status === "paused";

  const run = async (verb: "cancel" | "pause" | "resume"): Promise<void> => {
    setBusy(true);
    try {
      const result = await manage({ orderId: row.orderId, verb });
      switch (result.kind) {
        case "ok":
          toast.success(
            verb === "resume"
              ? "Your membership continues."
              : verb === "pause"
                ? "Paused at the end of this month."
                : "Cancelled — you keep the month you paid for.",
          );
          break;
        case "not_found":
          toast.error("We couldn't find that subscription.");
          break;
        case "retryable_error":
          toast.error("Checkout is busy", {
            description: "Please try again in a moment.",
          });
          break;
        case "failed":
          toast.error("That didn't go through", {
            description: "Please try again.",
          });
          break;
      }
    } catch (error) {
      toast.error("That didn't go through", {
        description: errorMessage(error, "Please try again."),
      });
    } finally {
      setBusy(false);
    }
  };

  // A signup that never got paid is not a membership — it is an unfinished
  // errand. `incomplete` belongs here beside `pending`, not with the lapsed
  // memberships: it is what an abandoned checkout becomes once the RETURN PAGE
  // sees the dead session, so the same reader doing the same thing lands in one
  // state or the other purely by whether they closed the tab or came back
  // through the redirect. Stamping that "Lapsed" would mourn a membership they
  // never had.
  if (row.status === "pending" || row.status === "incomplete") {
    return (
      <article className="border-border bg-card/60 rounded-lg border border-dashed p-5">
        <p className="text-muted-foreground font-serif text-sm">
          You started subscribing to{" "}
          <span className="text-foreground">{row.displayName}</span> but the
          checkout wasn’t finished. Nothing was charged.
        </p>
        {row.checkoutUrl !== null && row.status === "pending" ? (
          <Button asChild size="sm" variant="outline" className="mt-3">
            <a href={row.checkoutUrl}>Finish subscribing</a>
          </Button>
        ) : (
          // The hosted page is gone (expired, or we never recorded one), so
          // the honest affordance is a fresh start, not a dead link.
          <p className="text-muted-foreground mt-2 font-serif text-xs">
            Pick a plan below to start again.
          </p>
        )}
      </article>
    );
  }

  return (
    <article
      className={cn(
        "relative overflow-hidden rounded-lg border bg-card p-6",
        RULED,
        row.live ? "border-foreground/25" : "border-border opacity-80",
      )}
    >
      {/* The rubber stamp. NOT aria-hidden: it is the card's status, and the
          only other place that status appears is a "renews/ended" clause that
          itself depends on there being a date. The word carries the meaning,
          the rotation is just ink. */}
      <span
        className={cn(
          "absolute top-5 right-5 -rotate-6 rounded-sm border px-2 py-0.5",
          "font-serif text-[0.6rem] tracking-[0.25em] uppercase",
          row.live
            ? "border-foreground/30 text-foreground/70"
            : "border-muted-foreground/40 text-muted-foreground",
        )}
      >
        {stampLabel(row)}
      </span>

      <h3 className="font-display text-foreground pr-24 text-xl font-black tracking-tight">
        {row.displayName}
      </h3>
      <p className="text-muted-foreground mt-2 font-serif text-sm">
        <span className="font-rockwell text-foreground tabular-nums">
          {formatPrice(row.amountCents, row.currency)}
        </span>{" "}
        a month
        {row.entitledThrough !== null && (
          <>
            {" · "}
            {periodClause(row, formatDay(row.entitledThrough))}
          </>
        )}
      </p>

      {row.status === "past_due" && (
        <p className="text-destructive mt-3 font-serif text-sm">
          The last payment didn’t go through. Your card issuer may need to
          approve it; we’ll keep trying.
        </p>
      )}

      {/*
        Resume is offered whenever a pause is scheduled OR has taken effect —
        NOT only while the subscription is live. A pause that has bitten leaves
        `status: "paused"` past the paid window, which reads as not-live; gating
        the button on `live` made the one state whose entire purpose is to be
        undone the one state with no way to undo it.
      */}
      {resumable && (
        <div className="mt-5 flex flex-wrap gap-2">
          <Button
            size="sm"
            variant="outline"
            aria-disabled={busy}
            onClick={() => {
              if (!busy) void run("resume");
            }}
          >
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <PlayCircle className="size-4" />
            )}
            {row.status === "paused"
              ? "Resume my membership"
              : "Keep my membership"}
          </Button>
        </div>
      )}

      {row.live && !resumable && (
        <div className="mt-5 flex flex-wrap gap-2">
          {/*
            `aria-disabled`, not `disabled`: disabling the focused button drops
            keyboard focus to <body>, and on success this whole group is
            replaced, so focus never comes back. Same call, and the same
            reasoning, as the gate chips in gloss-popover.tsx.
          */}
          <Button
            size="sm"
            variant="outline"
            aria-disabled={busy}
            onClick={() => {
              if (!busy) void run("pause");
            }}
          >
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <PauseCircle className="size-4" />
            )}
            Pause after this month
          </Button>
          <Button
            size="sm"
            variant="ghost"
            aria-disabled={busy}
            onClick={() => {
              if (!busy) void run("cancel");
            }}
            className="text-muted-foreground"
          >
            Cancel
          </Button>
        </div>
      )}

      {ending && row.live && (
        <p className="text-muted-foreground mt-3 font-serif text-xs">
          Nothing more will be charged. You keep everything through{" "}
          {row.entitledThrough !== null
            ? formatDay(row.entitledThrough)
            : "the end of the month"}
          .
        </p>
      )}
      {/*
        "Nothing was kept on file" is only true of a membership that is really
        over. It used to render for any non-live row, which included a
        `past_due` subscription Stripe is still dunning — telling a reader
        nothing is retained while a retry may yet charge them.
      */}
      {row.status === "canceled" && !row.live && (
        <p className="text-muted-foreground mt-3 font-serif text-xs">
          Subscribe again below whenever you like — nothing was kept on file.
        </p>
      )}
      {row.status === "past_due" && !row.live && (
        <p className="text-muted-foreground mt-3 font-serif text-xs">
          Access has run out while the payment is retried. If it goes through,
          your membership picks up where it left off.
        </p>
      )}
    </article>
  );
}
